import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import usePublicLinksBaseUrl from '../hooks/usePublicLinksBaseUrl';
import {
  FiPlus,
  FiTrash2,
  FiCopy,
  FiLink,
  FiSave,
  FiRefreshCcw,
  FiArrowUp,
  FiArrowDown,
  FiBarChart2,
  FiLoader,
  FiEdit3,
  FiDownload
} from 'react-icons/fi';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import './Evaluation.css';
import { showNotification } from './Notification';

const CHART_COLORS = ['#2563eb', '#10b981', '#f97316', '#f43f5e', '#8b5cf6', '#14b8a6'];

const QUESTION_TYPES = [
  {
    value: 'yes_no',
    label: 'Yes / Somewhat / No',
    supportsOptions: true,
    defaultOptions: ['Yes', 'Somewhat', 'No']
  },
  {
    value: 'single_choice',
    label: 'Single Choice (Radio)',
    supportsOptions: true,
    defaultOptions: ['Option 1', 'Option 2']
  },
  {
    value: 'multiple_choice',
    label: 'Multiple Choice (Checkboxes)',
    supportsOptions: true,
    defaultOptions: ['Option 1', 'Option 2']
  },
  {
    value: 'rating',
    label: 'Rating Scale (1 - 5)',
    supportsOptions: true,
    defaultOptions: ['1', '2', '3', '4', '5']
  },
  {
    value: 'text',
    label: 'Narrative / Text Answer',
    supportsOptions: false,
    defaultOptions: []
  }
];

const buildQuestion = (overrides = {}) => {
  const baseType = overrides.question_type || 'yes_no';
  const template = QUESTION_TYPES.find((type) => type.value === baseType);

  return {
    uid: overrides.uid || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    question_text: overrides.question_text || '',
    question_type: baseType,
    options:
      overrides.options && overrides.options.length > 0
        ? overrides.options
        : template?.defaultOptions || [],
    is_required: overrides.is_required !== undefined ? overrides.is_required : true,
    order_index: overrides.order_index ?? 0,
    id: overrides.id || null
  };
};

const DEFAULT_QUESTION_BLUEPRINTS = [
  {
    question_text: 'Did the course content meet your expectation?',
    question_type: 'yes_no'
  },
  {
    question_text: 'Will the knowledge you gained be helpful to you?',
    question_type: 'yes_no'
  },
  {
    question_text: 'Is the resource person knowledgeable on the subject matter?',
    question_type: 'yes_no'
  },
  {
    question_text: 'Was the resource person able to explain and illustrate concepts?',
    question_type: 'yes_no'
  },
  {
    question_text: 'Did the resource person respond to questions accurately?',
    question_type: 'yes_no'
  },
  {
    question_text: 'What recommendation(s) do you have for the resource person to improve on?',
    question_type: 'text'
  },
  {
    question_text: 'What is your overall rating of the resource person?',
    question_type: 'single_choice',
    options: ['Below Average', 'Average', 'Good', 'Excellent']
  }
];

const createDefaultQuestionSet = () =>
  DEFAULT_QUESTION_BLUEPRINTS.map((config, index) =>
    buildQuestion({
      ...config,
      order_index: index
    })
  );

const Evaluation = () => {
  const linksBaseUrl = usePublicLinksBaseUrl();
  const [meta, setMeta] = useState({
    title: 'Evaluation of Resource Person',
    course_title: '',
    resource_person: '',
    session_date: '',
    batch_id: '',
    description: ''
  });
  const [questions, setQuestions] = useState(createDefaultQuestionSet);
  const [batches, setBatches] = useState([]);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [responsesView, setResponsesView] = useState({
    open: false,
    loading: false,
    data: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [questionActionLoading, setQuestionActionLoading] = useState(false);
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [selectAllResponses, setSelectAllResponses] = useState(false);
  const [responsesActionLoading, setResponsesActionLoading] = useState(false);
  const [selectedRespondentId, setSelectedRespondentId] = useState(null);

  useEffect(() => {
    fetchBatches();
    fetchForms();
  }, []);

  useEffect(() => {
    setSelectedQuestionIds((prev) => prev.filter((uid) => questions.some((q) => q.uid === uid)));
  }, [questions]);

  useEffect(() => {
    if (!responsesView.data?.responses) return;
    setSelectedRespondentId(responsesView.data.responses[0]?.id || null);
    setSelectedResponses([]);
    setSelectAllResponses(false);
  }, [responsesView.data?.responses]);

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/batches');
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchForms = async () => {
    try {
      setFormsLoading(true);
      const response = await axios.get('/api/evaluations/forms');
      setForms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching evaluation forms:', error);
    } finally {
      setFormsLoading(false);
    }
  };

  const resetBuilder = () => {
    setMeta({
      title: 'Evaluation of Resource Person',
      course_title: '',
      resource_person: '',
      session_date: '',
      batch_id: '',
      description: ''
    });
    setQuestions(createDefaultQuestionSet());
    setSelectedFormId(null);
    setLinkInfo(null);
    clearQuestionSelection();
  };

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      buildQuestion({
        question_text: `New question ${prev.length + 1}`,
        order_index: prev.length
      })
    ]);
  };

  const updateQuestion = (uid, key, value) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.uid !== uid) return question;
        if (key === 'question_type') {
          const template = QUESTION_TYPES.find((type) => type.value === value);
          return {
            ...question,
            question_type: value,
            options:
              template?.supportsOptions && question.options?.length
                ? question.options
                : template?.defaultOptions || [],
            is_required: question.is_required
          };
        }
        if (key === 'options') {
          return { ...question, options: value };
        }
        return { ...question, [key]: value };
      })
    );
  };

  const removeQuestion = (uid) => {
    setQuestions((prev) => prev.filter((question) => question.uid !== uid));
  };

  const moveQuestion = (uid, direction) => {
    setQuestions((prev) => {
      const index = prev.findIndex((question) => question.uid === uid);
      if (index === -1) return prev;
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const reordered = [...prev];
      const [item] = reordered.splice(index, 1);
      reordered.splice(newIndex, 0, item);
      return reordered.map((question, idx) => ({ ...question, order_index: idx }));
    });
  };

  const handleOptionChange = (uid, optionIndex, value) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.uid !== uid) return question;
        const updatedOptions = [...(question.options || [])];
        updatedOptions[optionIndex] = value;
        return { ...question, options: updatedOptions };
      })
    );
  };

  const addOption = (uid) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.uid !== uid) return question;
        const updatedOptions = [...(question.options || []), `Option ${question.options.length + 1}`];
        return { ...question, options: updatedOptions };
      })
    );
  };

  const removeOption = (uid, optionIndex) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.uid !== uid) return question;
        const updatedOptions = (question.options || []).filter((_, idx) => idx !== optionIndex);
        return { ...question, options: updatedOptions.length ? updatedOptions : ['Option 1'] };
      })
    );
  };

  const toggleQuestionSelection = (uid) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const clearQuestionSelection = () => setSelectedQuestionIds([]);

  const deleteSelectedQuestions = async () => {
    const selected = questions.filter((question) => selectedQuestionIds.includes(question.uid));
    if (selected.length === 0) return;

    const persistedIds = selected.filter((q) => q.id).map((q) => q.id);

    try {
      setQuestionActionLoading(true);
      if (persistedIds.length > 0 && selectedFormId) {
        await axios.delete(`/api/evaluations/forms/${selectedFormId}/questions`, {
          data: { questionIds: persistedIds }
        });
      }

      setQuestions((prev) => prev.filter((question) => !selectedQuestionIds.includes(question.uid)));
      clearQuestionSelection();
      showNotification('Selected questions removed', 'success');
    } catch (error) {
      showNotification(error.response?.data?.error || 'Unable to delete selected questions.', 'error');
    } finally {
      setQuestionActionLoading(false);
    }
  };

  const responsesFormId = responsesView.data?.form?.id;
  const responsesList = responsesView.data?.responses || [];
  const questionLookup = useMemo(() => {
    if (!responsesView.data?.questions) return {};
    return responsesView.data.questions.reduce((acc, question) => {
      acc[question.id] = question;
      return acc;
    }, {});
  }, [responsesView.data?.questions]);
  const chartStats = responsesView.data?.stats || [];

  const toggleResponseSelection = (id) => {
    setSelectedResponses((prev) =>
      prev.includes(id) ? prev.filter((responseId) => responseId !== id) : [...prev, id]
    );
  };

  const handleSelectAllResponses = () => {
    if (!responsesList.length) return;
    if (selectAllResponses) {
      setSelectedResponses([]);
      setSelectAllResponses(false);
      return;
    }
    setSelectedResponses(responsesList.map((response) => response.id));
    setSelectAllResponses(true);
  };

  const deleteResponses = async (ids) => {
    const targetIds = Array.isArray(ids) ? ids : selectedResponses;
    if (!targetIds.length || !responsesFormId) return;
    try {
      setResponsesActionLoading(true);
      await axios.delete(`/api/evaluations/forms/${responsesFormId}/responses`, {
        data: { responseIds: targetIds }
      });
      showNotification('Responses deleted successfully.', 'success');
      setSelectedResponses([]);
      setSelectAllResponses(false);
      await openResponses(responsesFormId);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Unable to delete responses.', 'error');
    } finally {
      setResponsesActionLoading(false);
    }
  };

  const handleExportResponses = () => {
    if (!responsesList.length) {
      showNotification('No responses available to export.', 'info');
      return;
    }
    const rows = responsesList.map((response) => {
      const row = {
        Name: response.respondent_name || 'Anonymous',
        Identifier: response.respondent_identifier || '',
        Email: response.respondent_email || '',
        Submitted:
          response.submitted_at ? new Date(response.submitted_at).toLocaleString() : ''
      };
      const answers = response.answers || {};
      Object.entries(answers).forEach(([questionId, value]) => {
        const question = questionLookup[Number(questionId)];
        const column =
          question?.question_text || `Question ${Object.keys(row).length - 3}`;
        row[column] = Array.isArray(value) ? value.join(', ') : value;
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Responses');

    const safeTitle = (responsesView.data?.form?.course_title || 'evaluation')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_');
    XLSX.writeFile(workbook, `${safeTitle}_responses.xlsx`);
  };

  const selectedRespondent = useMemo(() => {
    if (!selectedRespondentId) return null;
    return responsesList.find((response) => response.id === selectedRespondentId) || null;
  }, [responsesList, selectedRespondentId]);

  const selectedRespondentAnswerList = useMemo(() => {
    if (!selectedRespondent) return [];
    const answers = selectedRespondent.answers || {};
    return (responsesView.data?.questions || []).map((question) => {
      const value = answers[question.id] ?? answers[String(question.id)];
      if (value === undefined || value === null || value === '') {
        return { question: question.question_text, answer: '—' };
      }
      return {
        question: question.question_text,
        answer: Array.isArray(value) ? value.join(', ') : value
      };
    });
  }, [selectedRespondent, responsesView.data?.questions]);

  const selectedRespondentChartData = useMemo(() => {
    if (!selectedRespondent) return [];
    const answers = selectedRespondent.answers || {};
    const tally = {};
    Object.values(answers).forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          const label = item || 'N/A';
          tally[label] = (tally[label] || 0) + 1;
        });
      } else if (typeof value === 'string' && value.trim()) {
        tally[value.trim()] = (tally[value.trim()] || 0) + 1;
      }
    });
    return Object.entries(tally).map(([option, count]) => ({ option, count }));
  }, [selectedRespondent]);

  const payloadQuestions = useMemo(
    () =>
      questions.map((question, index) => ({
        question_text: question.question_text,
        question_type: question.question_type,
        options: question.options,
        is_required: question.is_required,
        order_index: index
      })),
    [questions]
  );

  const persistForm = async () => {
    if (!meta.course_title || !meta.resource_person || !meta.session_date) {
      showNotification('Course title, resource person, and date are required.', 'error');
      return null;
    }

    if (!payloadQuestions.length) {
      showNotification('Add at least one question.', 'error');
      return null;
    }

    try {
      setSaving(true);
      const payload = { ...meta, questions: payloadQuestions };
      if (selectedFormId) {
        await axios.put(`/api/evaluations/forms/${selectedFormId}`, payload);
        showNotification('Evaluation updated successfully.', 'success');
        return selectedFormId;
      }
      const response = await axios.post('/api/evaluations/forms', payload);
      const newId = response.data?.id;
      if (newId) {
        setSelectedFormId(newId);
        showNotification('Evaluation saved successfully.', 'success');
        return newId;
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Unable to save evaluation.';
      showNotification(message, 'error');
      return null;
    } finally {
      setSaving(false);
      fetchForms();
    }
    return null;
  };

  const handleGenerateLink = async () => {
    if (!meta.batch_id) {
      showNotification('Select a batch to lock this evaluation.', 'error');
      return;
    }

    const formId = await persistForm();
    if (!formId) return;

    try {
      setGenerating(true);
      const response = await axios.post(`/api/evaluations/forms/${formId}/generate-link`);
      const token = response.data?.token;
      if (token) {
        const url = `${linksBaseUrl}/evaluation/${token}`;
        navigator.clipboard.writeText(url);
        setLinkInfo({ token, url });
        showNotification('Evaluation link generated and copied.', 'success');
        fetchForms();
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Unable to generate link.';
      showNotification(message, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleLoadForm = async (formId) => {
    try {
      setPrefillLoading(true);
      const response = await axios.get(`/api/evaluations/forms/${formId}`);
      const form = response.data?.form;
      const fetchedQuestions = response.data?.questions || [];
      if (form) {
        setSelectedFormId(form.id);
        setMeta({
          title: form.title || 'Evaluation of Resource Person',
          course_title: form.course_title || '',
          resource_person: form.resource_person || '',
          session_date: form.session_date ? form.session_date.substring(0, 10) : '',
          batch_id: form.batch_id ? String(form.batch_id) : '',
          description: form.description || ''
        });
        setLinkInfo(
          form.token
            ? {
                token: form.token,
                url: `${linksBaseUrl}/evaluation/${form.token}`
              }
            : null
        );
        setQuestions(
          fetchedQuestions.map((question, index) =>
            buildQuestion({
              ...question,
              order_index: index
            })
          )
        );
        clearQuestionSelection();
      }
    } catch (error) {
      showNotification('Unable to load evaluation.', 'error');
    } finally {
      setPrefillLoading(false);
    }
  };

  const copyLink = (token) => {
    if (!token) return;
    const url = `${linksBaseUrl}/evaluation/${token}`;
    navigator.clipboard.writeText(url);
    showNotification('Link copied to clipboard.', 'success');
  };

  const openResponses = async (formId) => {
    try {
      setResponsesView({ open: true, loading: true, data: null });
      const response = await axios.get(`/api/evaluations/forms/${formId}/responses`);
      setResponsesView({
        open: true,
        loading: false,
        data: response.data
      });
    } catch (error) {
      const message = error.response?.data?.error || 'Unable to fetch responses.';
      showNotification(message, 'error');
      setResponsesView({ open: false, loading: false, data: null });
    }
  };

  const closeResponses = () => {
    setResponsesView({ open: false, loading: false, data: null });
  };

  const filteredForms = useMemo(() => {
    if (!Array.isArray(forms)) return [];
    if (!searchTerm.trim()) return forms;
    const needle = searchTerm.toLowerCase();
    return forms.filter((form) => {
      const haystack = `${form.course_title || ''} ${form.resource_person || ''} ${form.batch_name || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [forms, searchTerm]);

  return (
    <div className="evaluation-page">
      <header className="evaluation-hero">
        <div>
          <p className="eyebrow">CSS FARMS IDEAS-TVET Quality Loop</p>
          <h1>Create evaluation flows that feel as smart as Google Forms</h1>
          <p>
            Configure batch-specific evaluation links, mix-and-match question types, and track response trends
            immediately after each course.
          </p>
        </div>
        <div className="hero-actions">
          <button className="btn ghost" onClick={resetBuilder}>
            <FiRefreshCcw /> Reset builder
          </button>
          <button className="btn primary" onClick={persistForm} disabled={saving}>
            {saving ? (
              <>
                <FiLoader className="spin" /> Saving...
              </>
            ) : (
              <>
                <FiSave /> Save draft
              </>
            )}
          </button>
        </div>
      </header>

      <div className="evaluation-grid">
        <section className="builder-panel">
          <div className="panel-card meta-card">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Evaluation context</p>
                <h2>Session & batch information</h2>
              </div>
              {prefillLoading && <FiLoader className="spin muted" />}
            </div>

            <div className="field-grid">
              <div className="form-group">
                <label>Evaluation Title</label>
                <input
                  type="text"
                  value={meta.title}
                  onChange={(e) => handleMetaChange('title', e.target.value)}
                  placeholder="Evaluation of Resource Person"
                />
              </div>
              <div className="form-group">
                <label>Course Title</label>
                <input
                  type="text"
                  value={meta.course_title}
                  onChange={(e) => handleMetaChange('course_title', e.target.value)}
                  placeholder="Team Building"
                />
              </div>
              <div className="form-group">
                <label>Name of Resource Person</label>
                <input
                  type="text"
                  value={meta.resource_person}
                  onChange={(e) => handleMetaChange('resource_person', e.target.value)}
                  placeholder="Mr. / Ms."
                />
              </div>
              <div className="form-group">
                <label>Course Date</label>
                <input
                  type="date"
                  value={meta.session_date}
                  onChange={(e) => handleMetaChange('session_date', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Batch (required before sharing)</label>
                <select value={meta.batch_id} onChange={(e) => handleMetaChange('batch_id', e.target.value)}>
                  <option value="">Select batch</option>
                  {batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group full">
                <label>Additional Instructions (optional)</label>
                <textarea
                  rows="2"
                  value={meta.description}
                  onChange={(e) => handleMetaChange('description', e.target.value)}
                  placeholder="Add any extra context for trainees..."
                />
              </div>
            </div>
          </div>

          <div className="panel-card questions-card">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Question designer</p>
                <h2>Mix question types with instant editing</h2>
              </div>
              <button className="btn ghost" onClick={addQuestion}>
                <FiPlus /> Add question
              </button>
            </div>

            {selectedQuestionIds.length > 0 && (
              <div className="question-selection-bar">
                <span>{selectedQuestionIds.length} selected</span>
                <button
                  className="btn danger small"
                  onClick={deleteSelectedQuestions}
                  disabled={questionActionLoading}
                >
                  {questionActionLoading ? (
                    <>
                      <FiLoader className="spin" /> Removing...
                    </>
                  ) : (
                    <>
                      <FiTrash2 /> Delete selected
                    </>
                  )}
                </button>
              </div>
            )}

            {questions.length === 0 && (
              <div className="empty-state">
                <p>No questions yet. Start building using the button above.</p>
              </div>
            )}

            <div className="question-stack">
              {questions.map((question, index) => {
                const typeMeta = QUESTION_TYPES.find((type) => type.value === question.question_type);
                return (
                  <div className="question-card" key={question.uid}>
                    <header>
                      <label className="question-select">
                        <input
                          type="checkbox"
                          checked={selectedQuestionIds.includes(question.uid)}
                          onChange={() => toggleQuestionSelection(question.uid)}
                        />
                      </label>
                      <div className="question-label">
                        <span>{index + 1}.</span>
                        <input
                          type="text"
                          value={question.question_text}
                          onChange={(e) => updateQuestion(question.uid, 'question_text', e.target.value)}
                          placeholder="Type your question..."
                        />
                      </div>
                      <div className="question-controls">
                        <select
                          value={question.question_type}
                          onChange={(e) => updateQuestion(question.uid, 'question_type', e.target.value)}
                        >
                          {QUESTION_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        <button
                          className="icon-btn"
                          onClick={() => moveQuestion(question.uid, 'up')}
                          title="Move up"
                        >
                          <FiArrowUp />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => moveQuestion(question.uid, 'down')}
                          title="Move down"
                        >
                          <FiArrowDown />
                        </button>
                        <button className="icon-btn danger" onClick={() => removeQuestion(question.uid)} title="Remove">
                          <FiTrash2 />
                        </button>
                      </div>
                    </header>

                    {typeMeta?.supportsOptions && (
                      <div className="options-list">
                        {(question.options || []).map((option, optionIndex) => (
                          <div className="option-item" key={`${question.uid}-option-${optionIndex}`}>
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(question.uid, optionIndex, e.target.value)}
                              placeholder={`Option ${optionIndex + 1}`}
                            />
                            <button
                              className="icon-btn danger"
                              onClick={() => removeOption(question.uid, optionIndex)}
                              title="Remove option"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        ))}
                        <button className="btn outline small" onClick={() => addOption(question.uid)}>
                          <FiPlus /> Add option
                        </button>
                      </div>
                    )}

                    <div className="question-footer">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={question.is_required}
                          onChange={(e) => updateQuestion(question.uid, 'is_required', e.target.checked)}
                        />
                        Required
                      </label>
                      <span className="chip">{typeMeta?.label || 'Question type'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel-card actions-card">
            <div className="builder-actions">
              <button className="btn ghost" onClick={resetBuilder}>
                <FiRefreshCcw /> Start fresh
              </button>
              <button className="btn outline" onClick={persistForm} disabled={saving}>
                {saving ? (
                  <>
                    <FiLoader className="spin" /> Saving...
                  </>
                ) : (
                  <>
                    <FiSave /> Save draft
                  </>
                )}
              </button>
              <button className="btn primary" onClick={handleGenerateLink} disabled={generating}>
                {generating ? (
                  <>
                    <FiLoader className="spin" /> Generating...
                  </>
                ) : (
                  <>
                    <FiLink /> Save & Generate link
                  </>
                )}
              </button>
            </div>
            {linkInfo && (
              <div className="link-preview">
                <p>Shareable link</p>
                <div className="link-row">
                  <code>{linkInfo.url}</code>
                  <button className="icon-btn" onClick={() => copyLink(linkInfo.token)} title="Copy link">
                    <FiCopy />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <aside className="history-panel">
          <div className="panel-card">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Evaluation history</p>
                <h2>Saved sessions & live links</h2>
              </div>
            </div>
            <div className="form-search">
              <input
                type="text"
                placeholder="Search by course, facilitator or batch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {formsLoading ? (
              <div className="empty-state">Loading evaluations...</div>
            ) : filteredForms.length === 0 ? (
              <div className="empty-state">
                <p>No evaluations saved yet.</p>
              </div>
            ) : (
              <ul className="forms-list">
                {filteredForms.map((form) => (
                  <li key={form.id} className="form-item">
                    <div className="form-meta">
                      <h3>{form.course_title}</h3>
                      <p>
                        Resource Person: <strong>{form.resource_person}</strong>
                      </p>
                      <p>
                        Batch: <strong>{form.batch_name || 'Not set'}</strong>
                      </p>
                      <div className="chips-row">
                        <span className={`status-chip ${form.status}`}>{form.status || 'draft'}</span>
                        <span className="status-chip neutral">
                          <FiBarChart2 /> {form.response_count || 0} responses
                        </span>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button className="btn outline small" onClick={() => handleLoadForm(form.id)}>
                        <FiEdit3 /> Edit
                      </button>
                      <button className="btn outline small" onClick={() => openResponses(form.id)}>
                        <FiBarChart2 /> Responses
                      </button>
                      <button
                        className="btn ghost small"
                        onClick={() => copyLink(form.token)}
                        disabled={!form.token}
                        title={form.token ? 'Copy evaluation link' : 'Generate link first'}
                      >
                        <FiLink /> Link
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {responsesView.open && (
        <div className="responses-modal">
          <div className="responses-content">
            <button className="close-btn" onClick={closeResponses}>
              ×
            </button>
            {responsesView.loading ? (
              <div className="empty-state">Loading responses...</div>
            ) : !responsesView.data ? (
              <div className="empty-state">No response data available yet.</div>
            ) : (
              <>
                <header>
                  <p className="eyebrow">Response summary</p>
                  <h2>{responsesView.data.form?.course_title}</h2>
                  <p>
                    {responsesView.data.form?.resource_person} ·{' '}
                    {responsesView.data.form?.session_date
                      ? new Date(responsesView.data.form.session_date).toLocaleDateString()
                      : 'Date TBD'}
                  </p>
                </header>
                <div className="responses-actions">
                  <div className="actions-left">
                    <button
                      className="btn outline small"
                      type="button"
                      onClick={handleExportResponses}
                      disabled={!responsesList.length}
                    >
                      <FiDownload /> Export Excel
                    </button>
                    <button
                      className="btn danger small"
                      type="button"
                      onClick={() => deleteResponses()}
                      disabled={!selectedResponses.length || responsesActionLoading}
                    >
                      {responsesActionLoading ? (
                        <>
                          <FiLoader className="spin" /> Deleting...
                        </>
                      ) : (
                        <>
                          <FiTrash2 /> Delete selected
                        </>
                      )}
                    </button>
                  </div>
                  <span>{selectedResponses.length} selected</span>
                </div>

                <div className="chart-section">
                  <h3>All responses overview</h3>
                  <div className="chart-grid">
                    {chartStats.map((stat, index) => {
                      const data = Object.entries(stat.totals || {}).map(([option, count]) => ({
                        option,
                        count
                      }));
                      return (
                        <div key={stat.question_id} className="chart-card">
                          <p>{stat.question_text}</p>
                          {data.length === 0 ? (
                            <span className="chip">No responses yet</span>
                          ) : (
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={data}>
                                <XAxis dataKey="option" hide />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              </BarChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="responses-table">
                  <h3>Recent submissions</h3>
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={selectAllResponses && responsesList.length > 0}
                              onChange={handleSelectAllResponses}
                            />
                          </th>
                          <th>Name</th>
                          <th>Identifier</th>
                          <th>Email</th>
                          <th>Submitted</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {responsesList.map((response) => (
                          <tr key={response.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedResponses.includes(response.id)}
                                onChange={() => toggleResponseSelection(response.id)}
                              />
                            </td>
                            <td>{response.respondent_name || 'Anonymous'}</td>
                            <td>{response.respondent_identifier || '—'}</td>
                            <td>{response.respondent_email || '—'}</td>
                            <td>
                              {response.submitted_at
                                ? new Date(response.submitted_at).toLocaleString()
                                : '—'}
                            </td>
                            <td className="response-actions">
                              <button
                                type="button"
                                className="icon-btn danger"
                                onClick={() => deleteResponses([response.id])}
                                disabled={responsesActionLoading}
                                title="Delete response"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="individual-section">
                  <div className="individual-header">
                    <div>
                      <h3>Per person insights</h3>
                      <p>Review a specific trainee’s submission with charts and detail.</p>
                    </div>
                    <select
                      value={selectedRespondentId || ''}
                      onChange={(e) =>
                        setSelectedRespondentId(e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      <option value="">Select a respondent</option>
                      {responsesList.map((response) => (
                        <option key={response.id} value={response.id}>
                          {response.respondent_identifier ||
                            response.respondent_name ||
                            `Submission ${response.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedRespondent ? (
                    <>
                      <div className="individual-info">
                        <div>
                          <span className="identity-label">Name</span>
                          <strong>{selectedRespondent.respondent_name || 'Anonymous'}</strong>
                        </div>
                        <div>
                          <span className="identity-label">Tag</span>
                          <strong>{selectedRespondent.respondent_identifier || '—'}</strong>
                        </div>
                        <div>
                          <span className="identity-label">Submitted</span>
                          <strong>
                            {selectedRespondent.submitted_at
                              ? new Date(selectedRespondent.submitted_at).toLocaleString()
                              : '—'}
                          </strong>
                        </div>
                      </div>
                      {selectedRespondentChartData.length > 0 && (
                        <div className="individual-chart">
                          <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={selectedRespondentChartData}>
                              <XAxis dataKey="option" hide />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="count"
                                fill={CHART_COLORS[0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      <div className="per-person-answers">
                        {selectedRespondentAnswerList.map((item) => (
                          <div key={item.question}>
                            <p>{item.question}</p>
                            <strong>{item.answer || '—'}</strong>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p>Select a respondent to view their detailed answers.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluation;


